import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateInput } from "@/components/DateInput";
import { PriceEditor } from "@/components/seat/PriceEditor";
import { api, errMsg } from "@/lib/api";
import { STATUS, dmyToISO, isoToDMY, fmtISO, todayISO } from "@/lib/seat";
import { defaultPrice, hasPrice, fmtRp } from "@/lib/price";

const EMPTY = () => ({ date: "", operator: "", status: "OPEN", remark: "", price: defaultPrice() });
const depLabel = (d) => `${fmtISO(d.date)}${d.operator ? ` ${d.operator}` : ""}`;

const CopyTargets = ({ others, value, onChange }) => (
  <div className="space-y-2">
    <div className="flex flex-wrap items-center justify-between gap-1">
      <Label>Salin harga ini ke tanggal lain</Label>
      {others.length > 0 && <button type="button" onClick={() => onChange(value.length === others.length ? [] : others.map((d) => d.id))} className="text-xs text-brand hover:underline" data-testid="seat-copy-select-all">{value.length === others.length ? "Batal pilih semua" : "Pilih semua"}</button>}
    </div>
    {others.length === 0 ? <p className="text-xs text-slate-400">Belum ada tanggal lain.</p> : (
      <div className="grid max-h-36 grid-cols-2 gap-1 overflow-y-auto rounded-md border border-slate-200 p-2 sm:grid-cols-3">
        {others.map((d) => (
          <label key={d.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-slate-50">
            <Checkbox checked={value.includes(d.id)} onCheckedChange={(c) => onChange(c ? [...value, d.id] : value.filter((x) => x !== d.id))} data-testid={`seat-copy-target-${d.id}`} />
            {depLabel(d)}
          </label>
        ))}
      </div>
    )}
  </div>
);

export const DepartureDialog = ({ state, onClose, onSaved }) => {
  const { open, product, dep } = state;
  const [form, setForm] = useState(EMPTY());
  const [targets, setTargets] = useState([]);
  const [busy, setBusy] = useState(false);
  const others = (product?.departures || []).filter((d) => d.id !== dep?.id && d.date >= todayISO());
  const sources = others.filter((d) => hasPrice(d.price));

  useEffect(() => {
    if (open) {
      setForm(dep ? { date: isoToDMY(dep.date), operator: dep.operator || "", status: dep.status, remark: dep.remark || "", price: dep.price || defaultPrice() } : EMPTY());
      setTargets([]);
    }
  }, [open, dep]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onClose(); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    const date = dmyToISO(form.date);
    if (!date) return toast.error("Tanggal tidak valid, format DD MM YYYY");
    const body = { ...form, date, price: hasPrice(form.price) || form.price.items.some((i) => i.amount != null) ? form.price : null };
    run(async () => {
      const { data } = dep ? await api.put(`/seat/departures/${dep.id}`, body) : await api.post(`/seat/products/${product.id}/departures`, body);
      if (targets.length) await api.post(`/seat/departures/${data.id}/copy-price`, { target_ids: targets });
    }, targets.length ? `Disimpan, harga disalin ke ${targets.length} tanggal` : dep ? "Tanggal diperbarui" : "Tanggal ditambahkan");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="seat-departure-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{dep ? "Ubah Tanggal Keberangkatan" : "Tambah Tanggal Keberangkatan"}</DialogTitle>
          <DialogDescription>{product?.name}{dep?.updated_by ? ` \u2022 terakhir diubah oleh ${dep.updated_by}` : ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Tanggal</Label><DateInput value={form.date} onChange={(v) => setForm({ ...form, date: v })} className="h-10 text-sm" data-testid="seat-dep-date" /></div>
            <div className="space-y-2"><Label>Operator</Label><Input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} placeholder="TX / GCT" data-testid="seat-dep-operator" /></div>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Object.entries(STATUS).map(([k, s]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, status: k })} data-testid={`seat-dep-status-${k}`}
                  className={`rounded-md border px-2 py-2 text-xs font-medium transition-shadow ${s.cls.replace("line-through", "")} ${form.status === k ? "ring-2 ring-brand ring-offset-1" : "opacity-70 hover:opacity-100"}`}>
                  {k !== "OPEN" && `[${s.short}] `}{s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2"><Label>Remark (opsional)</Label><Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} placeholder="Contoh: sisa 4 seat, tunggu konfirmasi operator" data-testid="seat-dep-remark" /></div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>Harga</Label>
              {sources.length > 0 && (
                <Select value="" onValueChange={(id) => setForm({ ...form, price: sources.find((d) => d.id === id).price })}>
                  <SelectTrigger className="h-8 w-full text-xs sm:w-60" data-testid="seat-price-source"><SelectValue placeholder="Ambil harga dari tanggal lain..." /></SelectTrigger>
                  <SelectContent>{sources.map((d) => <SelectItem key={d.id} value={d.id}>{depLabel(d)} &middot; {fmtRp(d.price.total)}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <PriceEditor price={form.price} onChange={(price) => setForm((f) => ({ ...f, price }))} />
          </div>
          <CopyTargets others={others} value={targets} onChange={setTargets} />
          <DialogFooter className="gap-2 sm:justify-between">
            {dep ? <Button type="button" variant="ghost" disabled={busy} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="seat-dep-delete"
              onClick={() => window.confirm("Hapus tanggal keberangkatan ini?") && run(() => api.delete(`/seat/departures/${dep.id}`), "Tanggal dihapus")}>Hapus tanggal</Button> : <span />}
            <Button type="submit" disabled={busy} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid="seat-dep-submit">{busy ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
